import React, { useEffect, useState, useCallback } from 'react';
import { X, ZoomIn, ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import { galleryImages } from '../data/mock';

export default function Gallery() {
  const [activeIndex, setActiveIndex] = useState(null);
  const isOpen = activeIndex !== null;

  const close = useCallback(() => setActiveIndex(null), []);
  const prev = useCallback(
    (e) => {
      e?.stopPropagation?.();
      setActiveIndex((i) => (i === null ? 0 : (i - 1 + galleryImages.length) % galleryImages.length));
    },
    []
  );
  const next = useCallback(
    (e) => {
      e?.stopPropagation?.();
      setActiveIndex((i) => (i === null ? 0 : (i + 1) % galleryImages.length));
    },
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handler);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handler);
    };
  }, [isOpen, close, prev, next]);

  if (!galleryImages.length) return null;

  return (
    <section
      id="gallery"
      className="relative py-20 md:py-32 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #08111f 0%, #0a1428 100%)' }}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
        <div className="text-center max-w-2xl mx-auto">
          <p className="section-eyebrow text-amber-400">06 — Gallery</p>
          <h2 className="mt-4 font-anton text-3xl sm:text-4xl md:text-6xl leading-[0.95] text-white">
            INSIDE <span className="text-blue-500">BODY LINE</span>
          </h2>
          <p className="mt-4 text-sm md:text-base text-white/60">
            A peek into our modern equipment, energetic community and everyday grind.
          </p>
        </div>

        {/* Grid: mobile 1 col, sm 2 cols, lg 3 cols, xl 5 cols */}
        <div className="mt-10 md:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {galleryImages.map((img, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className="group relative overflow-hidden rounded-2xl bg-slate-900 ring-1 ring-white/10 hover:ring-blue-500/50 transition-all duration-300 hover:-translate-y-1"
              aria-label={`Open image ${i + 1}`}
            >
              <div className="aspect-[4/5] w-full">
                <img
                  src={img.src}
                  alt={img.caption || `Body Line Gym ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/10 to-transparent" />
              <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-slate-950/60 backdrop-blur border border-white/15 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn className="w-4 h-4" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 flex items-center gap-2 text-white">
                <Camera className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium truncate">{img.caption}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[100] bg-slate-950/95 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 fade-up"
          onClick={close}
          role="dialog"
          aria-modal="true"
        >
          {/* Close */}
          <button
            onClick={close}
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center border border-white/15 z-20"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Prev */}
          <button
            onClick={prev}
            className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center border border-white/15 z-20"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Image */}
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={galleryImages[activeIndex].src}
              alt={galleryImages[activeIndex].caption}
              className="w-full max-h-[80vh] object-contain rounded-xl shadow-2xl"
            />
            <div className="mt-3 flex items-center justify-between text-white/80 text-sm">
              <span className="inline-flex items-center gap-2">
                <Camera className="w-4 h-4 text-blue-400" />
                {galleryImages[activeIndex].caption}
              </span>
              <span className="text-white/50">
                {activeIndex + 1} / {galleryImages.length}
              </span>
            </div>

            {/* Mobile prev/next below image */}
            <div className="mt-3 flex sm:hidden justify-center gap-3">
              <button
                onClick={prev}
                className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center border border-white/15"
                aria-label="Previous"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center border border-white/15"
                aria-label="Next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Next */}
          <button
            onClick={next}
            className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center border border-white/15 z-20"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </section>
  );
}
